import { message } from "antd";
import FormItem from "antd/lib/form/FormItem";
import { type TablePaginationConfig } from "antd/lib/table/interface";
import { type AxiosError } from "axios";
import moment from "moment";
import queryString from "query-string";
import { type ChangeEvent, Component } from "react";
import { type RouteComponentProps } from "react-router";

import { AppDefaultInfoContext } from "helpers/globalContext/AppDefaultInfoContext";

import { type ExceptionResponse } from "types/apiResponse/apiResponse";
import { BTLoginTypes } from "types/enum";

import { showApixErrorMessage } from "utilities/api/apix-handler";
import { NoAccessError } from "utilities/apiHandler";
import { routesWebforms } from "utilities/routesWebforms";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { btInfo } from "commonComponents/btWrappers/BTConfirm/BTConfirm";
import { BTExternalLink } from "commonComponents/btWrappers/BTExternalLink/BTExternalLink";
import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";
import BTSearch from "commonComponents/btWrappers/BTSearch/BTSearch";
import { BTTable, type IBTColumnProps } from "commonComponents/btWrappers/BTTable/BTTable";
import { BTTitle } from "commonComponents/btWrappers/BTTitle/BTTitle";
import { UserActivationStatusTag } from "commonComponents/entity/userActivation/UserActivationStatusTag/UserActivationStatusTag";
import { BTLoading } from "commonComponents/utilities/BTLoading/BTLoading";
import { SkeletonTable } from "commonComponents/utilities/SkeletonTable/SkeletonTable";

import {
    ActivationStatus,
    adminUsersOrgLinkUsersGetV2,
    type AdminUsersOrgLinkUsersGetV2Params,
    type OrgLinkUser,
} from "handlers";

import ImpersonationQRContent from "entity/admin/AdminBuilders/ImpersonationQRContent";
import "entity/admin/AdminBuilders/Logins/AdminBuildersCompactTable.less";
import { type ILoginHandler, LoginHandler } from "entity/login/Login/Login.api.handler";

import "./BTAdminContactsTable.less";

interface IBtAdminContactListProps extends RouteComponentProps {
    loginHandler?: ILoginHandler;
    sticky?: boolean | { topLevel?: number; bottomLevel?: number };
}

interface IBtAdminContactListPropsInternal extends IBtAdminContactListProps {
    builderId: number;
    loginTypes: BTLoginTypes[];
}

interface IBtAdminContactListState {
    contacts: OrgLinkUser[];
    fullContactList: OrgLinkUser[];
    pagination: TablePaginationConfig;
    tableLoading: boolean;
    isSendingPasswordResetEmail: boolean;
    searchString: string;
}

class BtAdminContactListInternal extends Component<
    IBtAdminContactListPropsInternal,
    IBtAdminContactListState
> {
    private defaultPageSize = 20;
    private defaultPage = 1;

    static defaultProps = {
        loginHandler: new LoginHandler(),
        loginTypes: [BTLoginTypes.CONTACT],
    };

    static contextType = AppDefaultInfoContext;
    declare context: React.ContextType<typeof AppDefaultInfoContext>;

    state: Readonly<IBtAdminContactListState> = {
        contacts: [],
        fullContactList: [],
        pagination: {
            defaultPageSize: this.defaultPageSize,
            defaultCurrent: this.defaultPage,
            total: 0,
            pageSizeOptions: ["20", "50", "75", "100", "250"],
            showTotal: (total: number, range: number[]) =>
                `${range[0]}-${range[1]} of ${total} items`,
            showSizeChanger: true,
            position: ["bottomRight"],
        },
        tableLoading: true,
        isSendingPasswordResetEmail: false,
        searchString: "",
    };

    componentDidMount() {
        void this.initialize();
    }

    private readonly initialize = async () => {
        try {
            await this.load();
            this.setState({ tableLoading: false });
        } catch (e) {
            this.setState(() => {
                throw e;
            });
        }
    };

    private load = async () => {
        const { builderId, loginTypes } = this.props;

        const params: AdminUsersOrgLinkUsersGetV2Params = { builderId, loginTypes };

        const result = await adminUsersOrgLinkUsersGetV2(params).catch(
            (ex: AxiosError<ExceptionResponse>) => {
                showApixErrorMessage(ex);
            },
        );

        if (result?.data.orgLinkUsers) {
            this.setState({
                contacts: result.data.orgLinkUsers,
                fullContactList: result.data.orgLinkUsers,
            });
        }
    };

    private onImpersonationClick = async (globalUserId: number, name: string) => {
        const response = await this.props.loginHandler
            ?.impersonationCode(globalUserId)
            .catch((err: Error) => {
                if (err instanceof NoAccessError) {
                    // need to prompt to login - this behavior is built in automatically in BTadmin, recreating here.
                    window.location.reload();
                } else if (err.message) {
                    void message.error(err.message);
                } else {
                    void message.error("There was a problem generating Mobile Code.");
                }
            });

        if (response) {
            const impersonationUrl = `${window.location.origin}/app/ml/${response.authCode}`;
            void btInfo({
                content: (
                    <ImpersonationQRContent
                        url={impersonationUrl}
                        name={name}
                        expiresInMinutes={response.expiresInMinutes}
                    />
                ),
                width: 520,
            });
        }
    };

    private getActivationStatusForSorting(activationStatus: ActivationStatus) {
        switch (activationStatus) {
            case ActivationStatus.Active:
                return 0;
            case ActivationStatus.Pending:
                return 1;
            case ActivationStatus.Disabled:
                return 2;
            case ActivationStatus.NotSent:
                return 3;
        }
    }
    private handleSearchChange = (e: ChangeEvent<HTMLInputElement> | string) => {
        let searchString: string;
        if (typeof e === "string") {
            searchString = e;
        } else {
            searchString = e.target.value;
        }
        // Filter as the user types so results update on each keystroke, matching the
        // other admin table searches.
        this.setState({
            searchString,
            contacts: this.filterContacts(searchString),
        });
    };

    private filterContacts = (searchString: string): OrgLinkUser[] => {
        const { fullContactList } = this.state;

        if (searchString === "") {
            return fullContactList;
        }

        const search = searchString.toLowerCase();

        return fullContactList.filter(
            (x) =>
                x.firstName.toLowerCase().includes(search) ||
                x.lastName.toLowerCase().includes(search) ||
                x.displayName.toLowerCase().includes(search) ||
                x.email.toLowerCase().includes(search) ||
                x.userID.toLowerCase().includes(search),
        );
    };

    private AbbreviatedColumn(props: { text: string }) {
        return (
            <BTPopover content={props.text}>
                <div className="BtAdminContactsTableEmailCell">{props.text}</div>
            </BTPopover>
        );
    }

    render() {
        if (!this.state) {
            return <SkeletonTable columns={8} rows={10} />;
        }

        const { contacts, pagination, tableLoading } = this.state;
        const { sticky } = this.props;

        const columnProps: IBTColumnProps<OrgLinkUser>[] = [];

        columnProps.push({
            title: "Login ID",
            key: "contactId",
            align: "left",
            width: "130px",
            render: (_, contact) => {
                return contact.activation === ActivationStatus.Active ? (
                    <BTExternalLink
                        href={routesWebforms.BTAdmin.getAdminAutoLoginLink(
                            BTLoginTypes.CONTACT,
                            contact.id,
                        )}
                        isUnderline={true}
                    >
                        {contact.id}
                    </BTExternalLink>
                ) : (
                    <>{contact.id}</>
                );
            },
            sorter: (a, b) => a.id - b.id,
        });

        columnProps.push({
            title: "Display Name",
            key: "displayName",
            align: "left",
            width: "130px",
            render: (_, contact) => {
                return <>{contact.displayName}</>;
            },
            sorter: (a, b) => a.displayName.localeCompare(b.displayName),
        });

        columnProps.push({
            title: "First Name",
            key: "firstName",
            align: "left",
            width: "130px",
            render: (_, contact) => {
                return <>{contact.firstName}</>;
            },
            sorter: (a, b) => a.firstName.localeCompare(b.firstName),
        });

        columnProps.push({
            title: "Last Name",
            key: "lastName",
            align: "left",
            width: "130px",
            render: (_, contact) => {
                return <>{contact.lastName}</>;
            },
            sorter: (a, b) => a.lastName.localeCompare(b.lastName),
        });

        columnProps.push({
            title: "Email",
            key: "email",
            align: "left",
            width: "180px",
            render: (_, contact) => {
                return this.AbbreviatedColumn({ text: contact.email });
            },
        });

        columnProps.push({
            title: "Username",
            key: "username",
            align: "left",
            width: "180px",
            render: (_, contact) => {
                return this.AbbreviatedColumn({ text: contact.userID });
            },
        });
        columnProps.push({
            title: "Activation Status",
            key: "activationStatus",
            align: "left",
            width: "130px",
            render: (_, contact) => {
                return (
                    <UserActivationStatusTag
                        activationStatus={contact.activation}
                        activationSent={
                            contact.activationSent ? moment(contact.activationSent) : undefined
                        }
                        activationConfirmed={
                            contact.activationConfirmed
                                ? moment(contact.activationConfirmed)
                                : undefined
                        }
                    />
                );
            },
            sorter: (a, b) =>
                this.getActivationStatusForSorting(a.activation) -
                this.getActivationStatusForSorting(b.activation),
        });
        columnProps.push({
            title: "Impersonation",
            key: "impersonation",
            align: "left",
            width: "130px",
            sorter: (a, b) => Number(!!a.userID) - Number(!!b.userID), // Sorts users that can be impersonated first
            render: (_, contact) => {
                return (
                    contact.userID && (
                        <BTButton
                            data-testid={`impersonation-submit-${contact.id}`}
                            type="secondary"
                            onClick={async () => {
                                await this.onImpersonationClick(contact.id, contact.displayName);
                            }}
                        >
                            Impersonate User
                        </BTButton>
                    )
                );
            },
        });

        return (
            <div className="BtAdminContactsTable">
                <BTTitle
                    level={2}
                    style={{ textAlign: "center" }}
                    data-testid="btAdminContactsTitle"
                >
                    Contacts
                </BTTitle>
                <FormItem label="Search by name, email, or username">
                    <BTSearch
                        id="nameSearch"
                        data-testid="nameSearch"
                        style={{ width: "25%", float: "left" }}
                        onChange={this.handleSearchChange}
                        onSearch={this.handleSearchChange}
                        value={this.state.searchString}
                        placeholder="Name, Email, or Username"
                    />
                </FormItem>
                {tableLoading ? (
                    <SkeletonTable columns={8} rows={10} />
                ) : (
                    <>
                        <BTTable
                            className="AdminBuildersCompactTable"
                            size="small"
                            dataSource={contacts}
                            columns={columnProps}
                            rowKey={(record) => `${record.id.toString()}`}
                            pagination={pagination}
                            sticky={sticky}
                            scroll={{ x: "max-content" }}
                        />
                        {this.state.isSendingPasswordResetEmail && (
                            <BTLoading displayMode="fixed" />
                        )}
                    </>
                )}
            </div>
        );
    }
}

export const BtAdminContactsTable: React.FunctionComponent<IBtAdminContactListProps> = (props) => {
    // builderId from props in btadmin is tied to job.  we need from params in btadmin context
    // in the future if this is reused elsewhere with different builderId context can be split up and passed differently here
    // eslint-disable-next-line deprecate/member-expression
    const parsedBuilderId = queryString.parse(props.location.search?.toLowerCase()).builderid;
    const builderId = parsedBuilderId && !isNaN(+parsedBuilderId) ? +parsedBuilderId : -1;

    return (
        <BtAdminContactListInternal
            {...props}
            loginTypes={[BTLoginTypes.CONTACT]}
            builderId={builderId}
            sticky={props.sticky}
        >
            {props}
        </BtAdminContactListInternal>
    );
};

export default BtAdminContactsTable;
